/**
 * fetch() with a hard timeout so a stalled connection rejects instead of
 * hanging forever.
 *
 * Why this exists: the DSP auth path (/auth/token, /auth/session) and every
 * authenticated call have no fetch timeout. When a request stalls (connection
 * never settles — not an HTTP error, which rejects cleanly), the awaiting
 * promise never resolves. In ClientTokenCache that orphans `inFlightPromise`,
 * which every subsequent caller then awaits forever — wedging the whole app on
 * an infinite loading spinner with no error. A bounded fetch turns that
 * permanent hang into a recoverable rejection: the cache clears, the error
 * propagates, and the existing error/retry UI can take over.
 *
 * On a timeout it retries once, but ONLY for idempotent methods (GET/HEAD): a
 * stalled socket usually clears on a fresh connection, while replaying a POST
 * could double-fire a server-side mutation. Real HTTP errors and non-idempotent
 * requests are never retried here — they reject on the first attempt.
 *
 * If the caller supplies its own `signal`, we defer to it and skip the timeout.
 *
 * `AbortSignal.timeout` is missing in jsdom and in browsers older than Chrome
 * 103 / Safari 16 / Firefox 100, so we fall back to a manual AbortController
 * + setTimeout there, rewrapping the resulting AbortError as a TimeoutError
 * so the retry check below still recognizes it.
 *
 * @param {string} url
 * @param {RequestInit} [options]
 * @param {number} [ms=15000] timeout per attempt, in milliseconds
 * @param {number} [retries=1] max retries on timeout (GET/HEAD only)
 * @returns {Promise<Response>}
 */
export async function fetchWithTimeout(url, options = {}, ms = 15000, retries = 1) {
  const method = (options.method || 'GET').toUpperCase();
  const canRetry = method === 'GET' || method === 'HEAD';
  const hasNativeTimeout = typeof AbortSignal.timeout === 'function';

  for (let attempt = 0; ; attempt++) {
    const controller = options.signal || hasNativeTimeout ? null : new AbortController();
    const timer = controller && setTimeout(() => controller.abort(), ms);
    try {
      const signal = options.signal ?? (hasNativeTimeout ? AbortSignal.timeout(ms) : controller.signal);
      return await fetch(url, { ...options, signal });
    } catch (err) {
      const abortedByUs = controller?.signal.aborted && err?.name === 'AbortError';
      const error = abortedByUs ? Object.assign(new Error('Request timed out'), { name: 'TimeoutError' }) : err;
      if (error.name !== 'TimeoutError' || !canRetry || attempt >= retries) {
        throw error;
      }
    } finally {
      clearTimeout(timer);
    }
  }
}
